import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Download, Printer, Settings2 } from 'lucide-react'
import { getIncomeStatementGaap } from '../../api/accounting'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { HelpTip } from '../../components/HelpTip'
import { PERIODS, ReportsTabs, StatementHead, StatementTable, exportRowsCsv, incomeRows, periodDates, periodLabel, useCompanyName } from './reportsShared'
import { printPage, useAutoPrint } from '../../utils/print'

const COMPARE = [
  { id: 'previous', label: 'Previous period' },
  { id: 'last_year', label: 'Same period last year' },
  { id: 'none', label: 'No comparison' },
]

// Multi-step income statement (GAAP): net revenue (products and services),
// cost of revenue (cost of goods sold, cost of services), gross profit,
// operating expenses, operating income, other income and expenses, income
// before tax, income tax, net income.
export default function IncomeStatement() {
  const company = useCompanyName()
  const [params, setParams] = useSearchParams()
  const period = PERIODS.some((p) => p.id === params.get('period')) ? params.get('period') : 'this_month'
  const [custom, setCustom] = useState(() => {
    const [s, e] = periodDates('this_month')
    return { start: params.get('start') || s, end: params.get('end') || e }
  })
  const [start, end] = period === 'custom' ? [custom.start, custom.end] : periodDates(period)
  const compare = COMPARE.some((c) => c.id === params.get('compare')) ? params.get('compare') : (period === 'ytd' || period === 'last_year' ? 'last_year' : 'previous')
  const [data, setData] = useState(null)
  useAutoPrint(!!data)
  const [error, setError] = useState('')
  const [openAll, setOpenAll] = useState(false)
  const set = (patch) => setParams((p) => { const n = new URLSearchParams(p); Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); return n }, { replace: true })

  useEffect(() => {
    if (!start || !end) return
    setError('')
    setData(null)
    getIncomeStatementGaap({ start, end, compare })
      .then(setData)
      .catch((e) => setError(e.message || 'Could not load the income statement.'))
  }, [start, end, compare])

  const rows = useMemo(() => (data ? incomeRows(data) : []), [data])

  const currentLabel = data ? (period === 'custom' ? 'This period' : PERIODS.find((p) => p.id === period).label) : ''
  const priorLabel = data?.prior_period ? `${COMPARE.find((c) => c.id === compare).label}` : ''

  return (
    <div className="ci-page fs-page">
      <ReportsTabs />
      <div className="ci-header-row fs-noprint">
        <div className="ci-title-block">
          <div>
            <h1>Income statement <HelpTip k="incomeStatement" /></h1>
            <div className="ci-title-meta">Profit or loss for a period, step by step.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={() => exportRowsCsv(rows, { name: 'income_statement', currentLabel: `${start} to ${end}`, priorLabel: data?.prior_period ? `${data.prior_period.start} to ${data.prior_period.end}` : '' })}><Download size={15} />Export</button>
          <button type="button" className="ci-btn ci-btn-ghost" disabled={!data} onClick={() => printPage()}><Printer size={15} />Print</button>
        </div>
      </div>

      <div className="ci-toolbar fs-noprint">
        <select className="ci-select" value={period} onChange={(e) => set({ period: e.target.value })} aria-label="Period">
          {PERIODS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {period === 'custom' && (
          <>
            <input className="ci-select" type="date" value={custom.start} onChange={(e) => { setCustom((c) => ({ ...c, start: e.target.value })); set({ start: e.target.value }) }} aria-label="From" />
            <input className="ci-select" type="date" value={custom.end} onChange={(e) => { setCustom((c) => ({ ...c, end: e.target.value })); set({ end: e.target.value }) }} aria-label="To" />
          </>
        )}
        <select className="ci-select" value={compare} onChange={(e) => set({ compare: e.target.value })} aria-label="Compare with">
          {COMPARE.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={() => setOpenAll((v) => !v)}>{openAll ? 'Hide accounts' : 'Show accounts'}</button>
      </div>

      {error && <ErrorBanner message={error} />}

      {!data ? (!error && <Spinner label="Preparing the income statement…" />) : (
        <section className="fs-card">
          <StatementHead company={company} title="Income statement" sub={`For the period ${periodLabel(data.period.start, data.period.end)}`} />
          <StatementTable key={`${start}-${end}-${compare}`} rows={rows} currentLabel={currentLabel} priorLabel={priorLabel} openAll={openAll} glRange={[start, end]} />
          {data.prior_period && <p className="fs-foot">{priorLabel}: {periodLabel(data.prior_period.start, data.prior_period.end)}.</p>}
          <p className="fs-foot fs-noprint">Accounts are placed in sections automatically. <Link to="/accounting/reports/classification"><Settings2 size={12} aria-hidden="true" /> Account classification</Link></p>
        </section>
      )}
    </div>
  )
}
